<script setup lang="ts">
import { Form, Head } from '@inertiajs/vue3';
import InputError from '@/components/InputError.vue';
import PasswordInput from '@/components/PasswordInput.vue';
import TextLink from '@/components/TextLink.vue';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { register } from '@/routes';
import { store } from '@/routes/login';
import { request } from '@/routes/password';

defineOptions({
    layout: {
        title: 'Connectez-vous a votre compte',
        description:
            'Saisissez votre email et votre mot de passe pour continuer',
    },
});

defineProps<{
    status?: string;
    canResetPassword: boolean;
    canRegister: boolean;
}>();
</script>

<template>
    <Head title="Connexion" />

    <div
        v-if="status"
        class="mb-6 rounded-2xl border border-brand/20 bg-brand/[0.08] px-4 py-3 text-sm font-medium text-brand"
    >
        {{ status }}
    </div>

    <Form
        v-bind="store.form()"
        :reset-on-success="['password']"
        v-slot="{ errors, processing }"
        class="flex flex-col gap-8"
    >
        <div class="grid gap-5">
            <div class="grid gap-2.5">
                <div class="flex items-center justify-between gap-3">
                    <Label for="email">Adresse email</Label>
                    <span
                        class="text-xs font-medium tracking-[0.16em] text-muted-foreground uppercase"
                    >
                        Obligatoire
                    </span>
                </div>
                <Input
                    id="email"
                    type="email"
                    name="email"
                    required
                    autofocus
                    :tabindex="1"
                    autocomplete="email"
                    placeholder="email@example.com"
                    class="h-11 rounded-xl border-border/70 bg-background/70 px-4 shadow-none focus-visible:border-brand/40 focus-visible:ring-brand/15"
                />
                <InputError :message="errors.email" />
            </div>

            <div class="grid gap-2.5">
                <div class="flex items-center justify-between">
                    <Label for="password">Mot de passe</Label>
                    <TextLink
                        v-if="canResetPassword"
                        :href="request()"
                        class="text-sm text-brand decoration-brand/30 hover:decoration-brand"
                        :tabindex="5"
                    >
                        Mot de passe oublie ?
                    </TextLink>
                </div>
                <PasswordInput
                    id="password"
                    name="password"
                    required
                    :tabindex="2"
                    autocomplete="current-password"
                    placeholder="Mot de passe"
                    class="h-11 rounded-xl border-border/70 bg-background/70 px-4 shadow-none focus-visible:border-brand/40 focus-visible:ring-brand/15"
                />
                <InputError :message="errors.password" />
            </div>

            <div
                class="flex items-center justify-between rounded-2xl border border-border/70 bg-muted/30 px-4 py-3"
            >
                <Label
                    for="remember"
                    class="flex items-center space-x-3 text-sm text-muted-foreground"
                >
                    <Checkbox id="remember" name="remember" :tabindex="3" />
                    <span>Se souvenir de moi</span>
                </Label>
                <span class="text-xs text-muted-foreground">
                    Garder cet appareil de confiance
                </span>
            </div>

            <Button
                type="submit"
                variant="brand"
                size="lg"
                class="mt-2 w-full rounded-full"
                :tabindex="4"
                :disabled="processing"
                data-test="login-button"
            >
                <Spinner v-if="processing" />
                Se connecter
            </Button>
        </div>

        <div
            class="border-t border-border/60 pt-5 text-center text-sm text-muted-foreground"
            v-if="canRegister"
        >
            Vous n'avez pas encore de compte ?
            <TextLink
                :href="register()"
                class="ml-1 text-brand decoration-brand/30 hover:decoration-brand"
                :tabindex="5"
            >
                Creer un compte
            </TextLink>
        </div>
    </Form>
</template>
